import { useMemo } from 'react';

import cn from 'classnames';

import { Link } from 'libs/router/Link';

import type { Additionals } from 'stores/content/content.types';

import { useImage } from 'hooks/useImage';

import { Icon } from 'ui/atoms/Icon/Icon';

import styles from './CardsPosts.module.scss';

const baseClass = 'gdb-cards-posts';

export type Card = {
  imgSrc: string;
  title: string;
  meta?: string;
  slug?: string;
  buttonText?: string;
  description?: string;
  className?: string;
  hasBackground?: boolean;
};

type Props = {
  hasBackground?: boolean;
  className?: string;
  data: NonNullable<Additionals['articlesList']>['data'];
};

export const CardPost = ({
  hasBackground = false,
  meta,
  imgSrc,
  title,
  description,
  buttonText,
  slug,
  className,
}: Card) => {
  const { buildUrl } = useImage();

  const buildSrc = useMemo(() => buildUrl({ src: imgSrc, width: 360, height: 202 }), [buildUrl, imgSrc]);

  return (
    <Link
      draggable={false}
      route={slug}
      className={cn(styles[`${baseClass}-card`], hasBackground && styles[`${baseClass}-bg`], className)}
    >
      <div className={styles[`${baseClass}-image`]} style={{ backgroundImage: `url(${buildSrc})` }}></div>

      <div className={styles[`${baseClass}-content`]}>
        <div className={styles[`${baseClass}-inner`]}>
          {meta && <div className={styles[`${baseClass}-meta`]}></div>}

          <h2 className={styles[`${baseClass}-title`]}>{title}</h2>
          <p className={styles[`${baseClass}-description`]}>{description}</p>
        </div>

        {buttonText && (
          <button className={styles[`${baseClass}-cta`]}>
            {buttonText}
            <Icon type="company-arrow-right" className={styles[`${baseClass}-arrow`]} />
          </button>
        )}
      </div>
    </Link>
  );
};

export const CardsPosts = ({ hasBackground = false, className, data = [] }: Props) => {
  return (
    <div className={cn(styles[`${baseClass}-wrapper`], className)}>
      {data.map((item, idx) => (
        <CardPost
          key={idx}
          title={item.title}
          imgSrc={item.background?.url || ''}
          buttonText="Read more"
          description={item.description}
          slug={item.slug}
          hasBackground={hasBackground}
        />
      ))}
    </div>
  );
};
